import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatEUR } from "@/lib/formatters";
import { Plus, Trash2, Layers, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

export function PricingSection({ form, setForm, isEditable, addLine, removeLine, updateLine, addExtra, removeExtra, updateExtra, addMultiItem, removeMultiItem, updateMultiItem, addSubItem, removeSubItem, updateSubItem, middleSlot }) {
  const isLumpSum = form.pricing_type === "lump_sum";

  return (
    <>
      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Quotazione</h2>
          {isEditable && (
            <div className="flex items-center gap-1 bg-zinc-100 rounded-sm p-0.5">
              <button onClick={() => setForm({ ...form, pricing_type: "itemized" })} data-testid="pricing-type-itemized"
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${!isLumpSum ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
                A Voci
              </button>
              <button onClick={() => setForm({ ...form, pricing_type: "lump_sum" })} data-testid="pricing-type-lump-sum"
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${isLumpSum ? "bg-white text-[#002FA7] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
                A Corpo
              </button>
            </div>
          )}
        </div>

        {isLumpSum ? (
          <>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-500">Importo Forfettario</Label>
              <Input data-testid="lump-sum-amount" type="number" min="0" step="0.01" placeholder="Importo a corpo (€)" value={form.lump_sum_amount}
                onChange={(e) => setForm({ ...form, lump_sum_amount: parseFloat(e.target.value) || 0 })} disabled={!isEditable} className="rounded-sm text-lg font-semibold max-w-xs" />
            </div>

            {/* Single line items (description + qty only, no price) */}
            <div className="border-t border-zinc-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400 font-medium">Elementi Singoli — {form.line_items.length} {form.line_items.length === 1 ? "voce" : "voci"}</span>
                {isEditable && (
                  <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm h-8" data-testid="add-line-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Aggiungi
                  </Button>
                )}
              </div>
              <div className="space-y-3 mt-3">
                {form.line_items.map((li, idx) => (
                  <div key={li.id || idx} data-testid={`line-item-${idx}`} className="flex flex-col sm:flex-row gap-2 sm:items-start p-3 sm:p-0 bg-zinc-50/50 sm:bg-transparent rounded-sm">
                    <Input placeholder="Descrizione" value={li.description} onChange={(e) => updateLine(idx, "description", e.target.value)} disabled={!isEditable} className="rounded-sm flex-1" />
                    <div className="flex gap-2 items-center">
                      <Input type="number" placeholder="Qtà" value={li.quantity} onChange={(e) => updateLine(idx, "quantity", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-20" />
                      {isEditable && form.line_items.length > 1 && (
                        <button onClick={() => removeLine(idx)} className="p-2 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Multi items (description + sub-items only, no price) */}
            <div className="border-t border-zinc-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5" /> Elementi Multipli — {(form.multi_items || []).length} grupp{(form.multi_items || []).length === 1 ? "o" : "i"}
                </span>
                {isEditable && (
                  <Button size="sm" variant="outline" onClick={addMultiItem} className="rounded-sm h-8" data-testid="add-multi-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Elemento Multiplo
                  </Button>
                )}
              </div>
              <div className="space-y-3 mt-3">
                {(form.multi_items || []).map((mi, miIdx) => (
                  <MultiItemCard key={`lump-mi-${miIdx}`} mi={mi} miIdx={miIdx} isEditable={isEditable} hidePrice
                    updateMultiItem={updateMultiItem} removeMultiItem={removeMultiItem}
                    addSubItem={addSubItem} removeSubItem={removeSubItem} updateSubItem={updateSubItem} />
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Single line items */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-400 font-medium">Elementi Singoli — {form.line_items.length} {form.line_items.length === 1 ? "voce" : "voci"}</span>
              {isEditable && (
                <Button size="sm" variant="outline" onClick={addLine} className="rounded-sm h-8" data-testid="add-line-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Aggiungi
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {form.line_items.map((li, idx) => (
                <div key={li.id || idx} data-testid={`line-item-${idx}`} className="flex flex-col sm:flex-row gap-2 sm:items-start p-3 sm:p-0 bg-zinc-50/50 sm:bg-transparent rounded-sm">
                  <Input placeholder="Descrizione" value={li.description} onChange={(e) => updateLine(idx, "description", e.target.value)} disabled={!isEditable} className="rounded-sm flex-1" />
                  <div className="flex gap-2 items-center">
                    <Input type="number" placeholder="Qtà" value={li.quantity} onChange={(e) => updateLine(idx, "quantity", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-20" />
                    <Input type="number" placeholder="Prezzo (€)" value={li.unit_price} onChange={(e) => updateLine(idx, "unit_price", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-28" />
                    <span className="text-sm text-zinc-500 w-24 text-right whitespace-nowrap">{formatEUR((li.quantity || 0) * (li.unit_price || 0))}</span>
                    {isEditable && form.line_items.length > 1 && (
                      <button onClick={() => removeLine(idx)} className="p-2 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Multi items */}
            <div className="border-t border-zinc-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5" /> Elementi Multipli — {(form.multi_items || []).length} grupp{(form.multi_items || []).length === 1 ? "o" : "i"}
                </span>
                {isEditable && (
                  <Button size="sm" variant="outline" onClick={addMultiItem} className="rounded-sm h-8" data-testid="add-multi-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Elemento Multiplo
                  </Button>
                )}
              </div>
              <div className="space-y-3 mt-3">
                {(form.multi_items || []).map((mi, miIdx) => (
                  <MultiItemCard key={`item-mi-${miIdx}`} mi={mi} miIdx={miIdx} isEditable={isEditable}
                    updateMultiItem={updateMultiItem} removeMultiItem={removeMultiItem}
                    addSubItem={addSubItem} removeSubItem={removeSubItem} updateSubItem={updateSubItem} />
                ))}
              </div>
            </div>

            {/* Forfait aggiuntivo — solo se delivery_type=deposito (i.e. "Scarico Deposito") */}
            {form.delivery_type === "deposito" && (
              <div className="border-t border-zinc-200 pt-4 mt-4 space-y-3" data-testid="extra-lump-sum-section">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full" /> Quotazione forfettaria aggiuntiva
                  </span>
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Solo con Scarico Deposito</span>
                </div>

                {/* Righe descrittive (descrizione + quantità) */}
                <div className="space-y-2" data-testid="extra-lump-sum-items-list">
                  {(form.extra_lump_sum_items || []).map((it, i) => (
                    <div key={`extra-row-${i}`} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center" data-testid={`extra-lump-sum-item-${i}`}>
                      <Input
                        data-testid={`extra-lump-sum-item-${i}-desc`}
                        placeholder="Descrizione"
                        value={it.description || ""}
                        onChange={(e) => {
                          const next = [...form.extra_lump_sum_items];
                          next[i] = { ...next[i], description: e.target.value };
                          setForm({ ...form, extra_lump_sum_items: next });
                        }}
                        disabled={!isEditable}
                        className="rounded-sm"
                      />
                      <Input
                        data-testid={`extra-lump-sum-item-${i}-qty`}
                        type="number" inputMode="decimal" min="0" step="0.01"
                        placeholder="Qtà"
                        value={it.quantity ?? 1}
                        onChange={(e) => {
                          const next = [...form.extra_lump_sum_items];
                          next[i] = { ...next[i], quantity: parseFloat(e.target.value) || 0 };
                          setForm({ ...form, extra_lump_sum_items: next });
                        }}
                        disabled={!isEditable}
                        className="rounded-sm w-24 text-right"
                      />
                      {isEditable && (
                        <button
                          type="button"
                          onClick={() => {
                            const next = form.extra_lump_sum_items.filter((_, idx) => idx !== i);
                            setForm({ ...form, extra_lump_sum_items: next });
                          }}
                          data-testid={`extra-lump-sum-item-${i}-delete`}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-sm"
                          aria-label="Rimuovi riga"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {isEditable && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const next = [...(form.extra_lump_sum_items || []), { description: "", quantity: 1 }];
                        setForm({ ...form, extra_lump_sum_items: next });
                      }}
                      className="rounded-sm h-8"
                      data-testid="add-extra-lump-sum-item-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  )}
                </div>

                {/* Importo forfettario — separato, su riga propria */}
                <div className="bg-amber-50/40 border border-amber-200/60 rounded-sm p-3">
                  <Label htmlFor="extra-lump-amount" className="text-xs text-zinc-700 font-semibold uppercase tracking-wider">
                    Importo Forfettario (€)
                  </Label>
                  <Input
                    id="extra-lump-amount"
                    data-testid="extra-lump-sum-amount"
                    type="number" inputMode="decimal" min="0" step="0.01"
                    placeholder="0,00"
                    value={form.extra_lump_sum_amount || 0}
                    onChange={(e) => setForm({ ...form, extra_lump_sum_amount: parseFloat(e.target.value) || 0 })}
                    disabled={!isEditable}
                    className="rounded-sm w-40 mt-1.5 font-semibold text-lg"
                  />
                  <input
                    type="hidden"
                    data-testid="extra-lump-sum-label"
                    value={form.extra_lump_sum_label || "Quotazione forfettaria"}
                  />
                </div>

                <p className="text-[11px] text-zinc-400 leading-relaxed">
                  Le righe descrittive sopra sono informative. L'<strong>Importo Forfettario</strong> è il valore aggiunto al subtotale e mostrato in fattura.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {middleSlot}

      {/* Extra costs */}
      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold font-['Outfit'] text-zinc-500 uppercase tracking-wider">Costi Extra</h2>
          {isEditable && (
            <Button size="sm" variant="outline" onClick={addExtra} className="rounded-sm h-8" data-testid="add-extra-cost-btn">
              <Plus className="w-3 h-3 mr-1" /> Aggiungi
            </Button>
          )}
        </div>
        {form.extra_costs.map((ec, idx) => (
          <div key={ec.id || `extra-${idx}`} className="flex gap-2 items-center">
            <Input placeholder="Descrizione" value={ec.description} onChange={(e) => updateExtra(idx, "description", e.target.value)} disabled={!isEditable} className="rounded-sm flex-1" />
            <Input type="number" placeholder="Importo (€)" value={ec.amount} onChange={(e) => updateExtra(idx, "amount", parseFloat(e.target.value) || 0)} disabled={!isEditable} className="rounded-sm w-32" />
            {isEditable && <button onClick={() => removeExtra(idx)} className="p-2 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>}
          </div>
        ))}
      </div>
    </>
  );
}


function MultiItemCard({ mi, miIdx, isEditable, hidePrice, updateMultiItem, removeMultiItem, addSubItem, removeSubItem, updateSubItem }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-[#002FA7]/20 rounded-sm bg-blue-50/30" data-testid={`multi-item-${miIdx}`}>
      {/* Header row */}
      <div className="flex flex-col sm:flex-row gap-2 p-3 sm:items-center">
        <button type="button" onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-white/60 rounded-sm shrink-0 self-start">
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        <Input placeholder="Descrizione gruppo" value={mi.description}
          onChange={(e) => updateMultiItem(miIdx, "description", e.target.value)}
          disabled={!isEditable} className="rounded-sm flex-1 font-medium bg-white" data-testid={`multi-item-desc-${miIdx}`} />
        <div className="flex items-center gap-2">
          {!hidePrice && (
            <>
              <Input type="number" min="0" step="0.01" placeholder="Prezzo (€)" value={mi.price}
                onChange={(e) => updateMultiItem(miIdx, "price", parseFloat(e.target.value) || 0)}
                disabled={!isEditable} className="rounded-sm w-28 bg-white" data-testid={`multi-item-price-${miIdx}`} />
              <span className="text-sm font-medium text-[#002FA7] w-24 text-right whitespace-nowrap">{formatEUR(mi.price || 0)}</span>
            </>
          )}
          {isEditable && (
            <button onClick={() => removeMultiItem(miIdx)} className="p-2 hover:bg-red-50 rounded-sm" data-testid={`multi-item-delete-${miIdx}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {/* Sub-items */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 ml-6 sm:ml-9 space-y-2 border-t border-[#002FA7]/10">
          <div className="flex items-center justify-between pt-2">
            <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">Sotto-elementi ({(mi.sub_items || []).length})</span>
            {isEditable && (
              <button onClick={() => addSubItem(miIdx)} className="text-xs text-[#002FA7] hover:underline flex items-center gap-0.5" data-testid={`multi-item-add-sub-${miIdx}`}>
                <Plus className="w-3 h-3" /> Aggiungi
              </button>
            )}
          </div>
          {(mi.sub_items || []).map((si, siIdx) => (
            <div key={`sub-${miIdx}-${siIdx}`} className="flex gap-2 items-center" data-testid={`sub-item-${miIdx}-${siIdx}`}>
              <span className="text-xs text-zinc-300 w-5 shrink-0 text-right">{siIdx + 1}.</span>
              <Input placeholder="Descrizione elemento" value={si.description}
                onChange={(e) => updateSubItem(miIdx, siIdx, "description", e.target.value)}
                disabled={!isEditable} className="rounded-sm flex-1 h-8 text-sm bg-white" />
              <div className="flex items-center gap-1">
                <Input type="number" min="0" placeholder="Qtà" value={si.quantity}
                  onChange={(e) => updateSubItem(miIdx, siIdx, "quantity", parseInt(e.target.value) || 0)}
                  disabled={!isEditable} className="rounded-sm w-16 h-8 text-sm bg-white" />
                <span className="text-[10px] text-zinc-400">pz</span>
              </div>
              {isEditable && (mi.sub_items || []).length > 1 && (
                <button onClick={() => removeSubItem(miIdx, siIdx)} className="p-1 hover:bg-red-50 rounded-sm">
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
